import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { BatidaServidor, DocumentoOficial, Empregador, PortalServidorBatidas, SolicitacaoAjustePonto, SolicitacaoFerias, TipoDocumentoOficial } from '../../api.service';
import { ServicoAutenticacao } from '../../core/auth/servico-autenticacao';
import { atalhosDashboardServidor } from '../../core/rotas/navegacao-servidor';
import { AbaServidor, ServicoNavegacaoServidor } from '../../core/rotas/servico-navegacao-servidor';
import { LayoutServidorComponent } from '../../layout/layout-servidor.component';
import { PainelDashboardServidorComponent } from './painel-dashboard-servidor.component';
import { ServicoPortalServidor } from './servico-portal-servidor';

@Component({
  selector: 'app-tela-portal-servidor',
  standalone: true,
  imports: [CommonModule, FormsModule, LayoutServidorComponent, PainelDashboardServidorComponent],
  templateUrl: './tela-portal-servidor.component.html',
  styleUrls: ['./tela-portal-servidor.component.css', '../../shared/modal-pesquisa-lista.component.css']
})
export class TelaPortalServidorComponent implements OnInit, OnDestroy {
  aba: AbaServidor = 'inicio';
  agora = new Date();
  tenant?: Empregador;
  batidas?: PortalServidorBatidas;
  mensagem = '';
  senhaAtual = '';
  senhaNova = '';
  senhaConfirmacao = '';
  dataAjuste = '';
  horaAjuste = '';
  numeroBatidaAjuste: number | null = null;
  motivoAjuste = '';
  ajustes: SolicitacaoAjustePonto[] = [];
  dataInicioFerias = '';
  dataFimFerias = '';
  ferias: SolicitacaoFerias[] = [];
  tipoDocumento: TipoDocumentoOficial | null = null;
  documentos: DocumentoOficial[] = [];
  modalFinalidadeAberta = false;
  finalidadeDocumento = '';
  private atualizacaoRelogio?: number;
  private assinaturaRoteador?: Subscription;

  constructor(
    private readonly servicoAutenticacao: ServicoAutenticacao,
    private readonly servicoPortalServidor: ServicoPortalServidor,
    private readonly servicoNavegacaoServidor: ServicoNavegacaoServidor,
    private readonly roteador: Router
  ) {}

  get usuario() {
    return this.servicoAutenticacao.usuarioLogado;
  }

  get itensMenu() {
    return this.servicoNavegacaoServidor.itens();
  }

  get atalhos() {
    return atalhosDashboardServidor(Boolean(this.batidas?.podeOficios));
  }

  get identificacao() {
    const nome = this.batidas?.nome || this.usuario?.nome || '';
    const cpf = this.batidas?.cpf || '';
    return cpf ? `${nome} · ${cpf}` : nome;
  }

  get nomeEmpresa() {
    return this.tenant?.razaoSocial || this.tenant?.nomeFantasia || 'Ponto JWM';
  }

  get logoEmpresa() {
    return this.servicoPortalServidor.logo(this.tenant);
  }

  get iniciaisEmpresa() {
    return this.servicoPortalServidor.iniciais(this.nomeEmpresa);
  }

  get colunas() {
    return this.servicoPortalServidor.colunasBatida(this.batidas?.dias || []);
  }

  ngOnInit() {
    this.dataAjuste = this.servicoPortalServidor.dataIsoHoje();
    this.dataInicioFerias = this.dataAjuste;
    this.dataFimFerias = this.dataAjuste;
    this.atualizacaoRelogio = window.setInterval(() => this.agora = new Date(), 1000);
    this.sincronizarAba(this.roteador.url);
    this.assinaturaRoteador = this.roteador.events
      .pipe(filter((evento): evento is NavigationEnd => evento instanceof NavigationEnd))
      .subscribe(evento => this.sincronizarAba(evento.urlAfterRedirects));
    this.carregarIdentidade();
    this.carregarBatidas();
    this.carregarAjustes();
    this.carregarFerias();
    this.carregarDocumentos();
  }

  ngOnDestroy() {
    if (this.atualizacaoRelogio) {
      window.clearInterval(this.atualizacaoRelogio);
    }
    this.assinaturaRoteador?.unsubscribe();
  }

  selecionarAba(aba: string) {
    this.roteador.navigateByUrl(this.servicoNavegacaoServidor.rotaDaAba(aba as AbaServidor));
  }

  batidaNaColuna(dia: PortalServidorBatidas['dias'][number], coluna: number) {
    return this.servicoPortalServidor.batidaNaColuna(dia, coluna);
  }

  comprovante(batida?: BatidaServidor) {
    if (!batida?.nsr) {
      return;
    }
    this.servicoPortalServidor.baixarComprovante(batida.nsr).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      },
      error: () => this.mensagem = 'Nao foi possivel abrir o comprovante.'
    });
  }

  pedirAjuste() {
    const data = this.dataAjuste.trim();
    const hora = this.horaAjuste.trim().slice(0, 5);
    const motivo = this.motivoAjuste.trim();
    const numeroBatida = this.numeroBatidaAjuste;
    if (!data) {
      this.mensagem = 'Informe o dia da marcacao.';
      return;
    }
    if (!hora) {
      this.mensagem = 'Informe o horario da marcacao no formato HH:mm.';
      return;
    }
    if (numeroBatida == null || numeroBatida < 1 || numeroBatida > 4) {
      this.mensagem = 'Informe a batida.';
      return;
    }
    if (!motivo) {
      this.mensagem = 'Informe o motivo da inclusao.';
      return;
    }
    this.servicoPortalServidor.pedirAjuste(data, numeroBatida, hora, motivo).subscribe({
      next: () => {
        this.mensagem = 'Solicitacao enviada. Apos o deferimento, o horario sera gravado na folha oficial.';
        this.horaAjuste = '';
        this.numeroBatidaAjuste = null;
        this.motivoAjuste = '';
        this.carregarAjustes();
      },
      error: erro => this.mensagem = this.mensagemErro(erro, 'Nao foi possivel enviar a solicitacao.')
    });
  }

  abrirModalFinalidade() {
    if (!this.tipoDocumento) {
      this.mensagem = 'Selecione o tipo do documento.';
      return;
    }
    this.finalidadeDocumento = '';
    this.modalFinalidadeAberta = true;
  }

  fecharModalFinalidade() {
    this.modalFinalidadeAberta = false;
  }

  pedirDocumento() {
    const finalidade = this.finalidadeDocumento.trim();
    if (!this.tipoDocumento) {
      this.mensagem = 'Selecione o tipo do documento.';
      return;
    }
    if (!finalidade) {
      this.mensagem = 'Informe a finalidade.';
      return;
    }
    this.servicoPortalServidor.pedirDocumento(this.tipoDocumento, finalidade).subscribe({
      next: resposta => {
        this.mensagem = `${this.rotuloTipo(resposta.tipo)} ${resposta.numero}/${resposta.ano} registrado.`;
        this.tipoDocumento = null;
        this.finalidadeDocumento = '';
        this.fecharModalFinalidade();
        this.carregarDocumentos();
      },
      error: erro => this.mensagem = this.mensagemErro(erro, 'Nao foi possivel solicitar o numero.')
    });
  }

  formatarData(valor?: string) {
    return this.servicoPortalServidor.formatarData(valor);
  }

  rotuloTipo(tipo: TipoDocumentoOficial) {
    if (tipo === 'PORTARIA') {
      return 'Portaria';
    }
    if (tipo === 'DECRETO') {
      return 'Decreto';
    }
    return 'Ofício';
  }

  pedirFerias() {
    this.servicoPortalServidor.pedirFerias(this.dataInicioFerias, this.dataFimFerias).subscribe({
      next: resposta => {
        this.mensagem = `Solicitacao enviada. Protocolo ${resposta.protocolo}.`;
        this.carregarFerias();
      },
      error: erro => this.mensagem = this.mensagemErro(erro, 'Nao foi possivel enviar a solicitacao de ferias.')
    });
  }

  rotuloStatus(status: SolicitacaoAjustePonto['status']) {
    return this.servicoPortalServidor.rotuloStatus(status);
  }

  alterarSenha() {
    if (this.senhaNova.length < 6) {
      this.mensagem = 'A nova senha deve ter pelo menos 6 caracteres.';
      return;
    }
    if (this.senhaNova !== this.senhaConfirmacao) {
      this.mensagem = 'A confirmacao da senha nao confere.';
      return;
    }
    this.servicoPortalServidor.alterarSenha(this.senhaAtual, this.senhaNova).subscribe({
      next: () => {
        this.mensagem = 'Senha alterada.';
        this.senhaAtual = '';
        this.senhaNova = '';
        this.senhaConfirmacao = '';
      },
      error: () => this.mensagem = 'Nao foi possivel alterar a senha.'
    });
  }

  sair() {
    this.servicoAutenticacao.sair();
    this.roteador.navigate(['/servidor/login']);
  }

  private sincronizarAba(url: string) {
    this.aba = this.servicoNavegacaoServidor.abaPorUrl(url);
  }

  private carregarIdentidade() {
    this.tenant = this.usuario?.empregador;
    this.servicoPortalServidor.buscarTenant().subscribe({
      next: tenant => this.tenant = tenant,
      error: () => undefined
    });
  }

  private carregarBatidas() {
    this.servicoPortalServidor.buscarBatidas().subscribe({
      next: batidas => this.batidas = batidas,
      error: () => this.mensagem = 'Nao foi possivel carregar as batidas do mes.'
    });
  }

  private carregarAjustes() {
    this.servicoPortalServidor.listarAjustes().subscribe({
      next: ajustes => this.ajustes = ajustes,
      error: () => undefined
    });
  }

  private carregarFerias() {
    this.servicoPortalServidor.listarFerias().subscribe({
      next: ferias => this.ferias = ferias,
      error: () => undefined
    });
  }

  private carregarDocumentos() {
    this.servicoPortalServidor.listarDocumentos().subscribe({
      next: documentos => this.documentos = documentos,
      error: () => undefined
    });
  }

  private mensagemErro(erro: { error?: { detail?: string } }, padrao: string) {
    return erro?.error?.detail || padrao;
  }
}
